import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './SpamhausHistory.css';

const SpamhausHistory = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  useEffect(() => {
    fetchSubmissions(currentPage);
  }, [currentPage]);

  const fetchSubmissions = async (page) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Use environment variable for API base URL
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API_BASE_URL}/spamhaus/submissions?page=${page}&limit=10`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubmissions(data.submissions);
        setPagination(data.pagination);
      } else {
        console.error('Failed to fetch submissions');
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
    setLoading(false);
  };

  const fetchSubmissionDetails = async (submissionId) => {
    try {
      const token = localStorage.getItem('token');
      // Use environment variable for API base URL
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API_BASE_URL}/spamhaus/submissions/${submissionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedSubmission(data);
      } else {
        console.error('Failed to fetch submission details');
      }
    } catch (error) {
      console.error('Error fetching submission details:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'warning';
      case 'failed': return 'error';
      default: return 'secondary';
    }
  };

  const closeModal = () => {
    setSelectedSubmission(null);
  };

  return (
    <div className="spamhaus-history">
      <div className="history-header">
        <div className="header-content">
          <button onClick={() => navigate('/tools')} className="back-btn">
            ← Back to Tools
          </button>
          <div className="header-info">
            <h1>Spamhaus Submission History</h1>
            <p>View and track your domain submission history</p>
          </div>
          <button onClick={() => navigate('/spamhaus')} className="new-submission-btn">
            + New Submission
          </button>
        </div>
      </div>

      <div className="history-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner-large"></div>
            <p>Loading submission history...</p>
          </div>
        ) : submissions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>No Submissions Yet</h3>
            <p>You haven't submitted any domains to Spamhaus yet.</p>
            <button onClick={() => navigate('/spamhaus')} className="primary-btn">
              Submit Your First Domain
            </button>
          </div>
        ) : (
          <>
            <div className="submissions-table-container">
              <table className="submissions-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Domain(s)</th>
                    <th>Status</th>
                    <th>Threat Type</th>
                    <th>Submitted At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((submission, index) => (
                    <tr key={submission._id} className="submission-row">
                      <td className="serial-number">
                        {(currentPage - 1) * 10 + index + 1}
                      </td>
                      <td className="domains-cell">
                        <div className="domains-preview">
                          {submission.domains && submission.domains.length > 0 ? (
                            <>
                              <span className="primary-domain">{submission.domains[0].domain}</span>
                              {submission.domains.length > 1 && (
                                <span className="domain-count">
                                  +{submission.domains.length - 1} more
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="domain-count">{submission.totalDomains} domain(s)</span>
                          )}
                        </div>
                      </td>
                      <td className="status-cell">
                        <span className={`status-badge ${submission.successfulSubmissions === submission.totalDomains ? 'success' : submission.successfulSubmissions > 0 ? 'partial' : 'failed'}`}>
                          {submission.successfulSubmissions === submission.totalDomains ? 'Success' : 
                           submission.successfulSubmissions > 0 ? 'Partial' : 'Failed'}
                        </span>
                      </td>
                      <td className="threat-type-cell">
                        {submission.domains && submission.domains.length > 0 && submission.domains[0].threatType ? 
                          submission.domains[0].threatType : 'N/A'}
                      </td>
                      <td className="date-cell">
                        <div className="date-info">
                          <div className="date">{formatDate(submission.createdAt).split(' at ')[0]}</div>
                          <div className="time">{formatDate(submission.createdAt).split(' at ')[1]}</div>
                        </div>
                      </td>
                      <td className="actions-cell">
                        <button 
                          onClick={() => fetchSubmissionDetails(submission.submissionId)}
                          className="view-details-btn"
                          title="View Details"
                        >
                          👁️ View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination && pagination.totalPages > 1 && (
              <div className="pagination">
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={!pagination.hasPrevPage}
                  className="pagination-btn"
                >
                  Previous
                </button>
                
                <div className="pagination-info">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </div>
                
                <button 
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={!pagination.hasNextPage}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Submission Details Modal */}
      {selectedSubmission && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Submission Details</h3>
              <button onClick={closeModal} className="close-btn">×</button>
            </div>
            
            <div className="modal-body">
              <div className="submission-info">
                <div className="info-row">
                  <label>Submission ID:</label>
                  <span>{selectedSubmission.submissionId}</span>
                </div>
                <div className="info-row">
                  <label>Status:</label>
                  <span className={`status-badge ${getStatusColor(selectedSubmission.overallStatus)}`}>
                    {selectedSubmission.overallStatus}
                  </span>
                </div>
                <div className="info-row">
                  <label>Submitted:</label>
                  <span>{formatDate(selectedSubmission.createdAt)}</span>
                </div>
                <div className="info-row">
                  <label>Total Domains:</label>
                  <span>{selectedSubmission.totalDomains}</span>
                </div>
              </div>

              <div className="domains-list">
                <h4>Domain Details</h4>
                <div className="domains-table">
                  <div className="table-header">
                    <div>Domain</div>
                    <div>Status</div>
                    <div>Threat Type</div>
                    <div>Spamhaus ID</div>
                  </div>
                  {selectedSubmission.domains.map((domain, index) => (
                    <div key={index} className={`table-row ${domain.status}`}>
                      <div className="domain-name">{domain.domain}</div>
                      <div className="domain-status">
                        <span className={`status-badge ${domain.status === 'submitted' ? 'success' : domain.status === 'already_reported' ? 'warning' : 'error'}`}>
                          {domain.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="threat-type">{domain.threatType}</div>
                      <div className="spamhaus-id">
                        {domain.spamhausId ? (
                          <code>{domain.spamhausId}</code>
                        ) : (
                          <span className="no-id">-</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpamhausHistory;
